import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { IUser, IDropdown, IUserDropdown } from "../../models/BaseModel";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { JVInput } from "../../shared/JVInput";
import { JVCheckbox } from "../../shared/JVCheckbox";
import { JVMultiselect } from "../../shared/JVMultiselect";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVInputMask } from "../../shared/JVInputMask";

interface Iprops {
    singleData: IUser,
    open: boolean,
    hideDialog: () => void,
    toast: any,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean
}

export const Form = (props: Iprops) => {
    const [roles, setRoles] = useState<IDropdown[]>();
    const [resorts, setResorts] = useState<IDropdown[]>();
    const [departments, setDepartments] = useState<IDropdown[]>();
    const [selectedRoles, setSelectedRoles] = useState<IDropdown[]>();
    const [selectedResorts, setSelectedResorts] = useState<IDropdown[]>();
    const [submitting, setSubmitting] = useState(false);
    const [manager, setManager] = useState<IUserDropdown[]>();
    const [teamLead, setTeamLead] = useState<IUserDropdown[]>();

    const schema = yup.object({
        name: yup.string()
            .required("Please enter Name.")
            .min(3, "Name must be at least 3 characters long.")
            .max(12, "Name can't exceed 12 characters."),

        email: yup.string()
            .required("Please provide email address.")
            .email("Please enter a valid email address."),

        password: !props.singleData.id ? yup.string()
            .required("A password is required to proceed.")
            .min(6, "Password must be at least 6 characters long.") :
            yup.string().nullable(),

        isActive: yup
            .boolean()
            .default(true)
            .required('Please specify if the user is active.'),
        departmentId: yup.number().required("Department is required").min(1, "Department is required"),
        resortId: yup.number().required("Resort is required").min(1, "Resort is required")
    });

    const { control, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<IUser>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData,
        shouldFocusError: false,
    });

    const isTeamLead = watch("isTeamLead");

    useEffect(() => {
        loadRoles();
        loadResorts();
        loadDepartments();
        loadTeamLead();
        // loadManager();
    }, []);

    
      useEffect(() => {
        loadTeamLead();
    }, [props.reloadData]);

    useEffect(() => {
        if (isTeamLead) {
            setValue("teamLead_Id", null);
        }
    }, [isTeamLead, setValue]);

    const loadDepartments = async () => {
        const response = await Fetch("department/dropdown", "GET", null, null, null);
        if (response) {
            setDepartments(await response.json());
        }
    }

    const loadManager = async () => {
        const response = await Fetch("user/getManager", "GET", null, null, null);
        if (response) {
            // setManager(await response.json());
            const data = await response.json();
            setManager([
                { id: null, name: '-- Select Manager --' },
                ...data
            ]);
        }
    }

    const loadTeamLead = async () => {
        const response = await Fetch("user/teamlead", "GET", null, null, null);
        if (response) {
            const data = await response.json();

            setTeamLead([
                { id: null, name: '-- Select Team Lead --' },
                ...data
            ]);
        }
    }

    useEffect(() => {
        reset(props.singleData);
        if (props.singleData.id) {
            loadAssignedRoles(props.singleData.id);
            loadAssignedResorts(props.singleData.id);
        } else {
            setSelectedRoles([]);
            setSelectedResorts([]);
        }
    }, [props.singleData]);

    const loadResorts = async () => {
        const response = await Fetch("resort/dropdown", "GET", null, null, null);
        if (response) {
            setResorts(await response.json());
        }
    }

    const loadAssignedResorts = async (id: number) => {
        const response = await Fetch(`user/resorts/${id}`, "GET", null, null, null);
        if (response) {
            setSelectedResorts(await response.json());
        }
    }

    const loadRoles = async () => {
        const response = await Fetch("role/dropdown", "GET", null, null, null);
        if (response) {
            setRoles(await response.json());
        }
    }

    const loadAssignedRoles = async (id: number) => {
        const response = await Fetch(`user/roles/${id}`, "GET", null, null, null);
        if (response) {
            setSelectedRoles(await response.json());
        }
    }

    const saveToDB = async (body: IUser) => {
        setSubmitting(true);
        const method = props.singleData.id ? "PUT" : "POST";
        const message = props.singleData.id ? "Updated Successfully" : "Added Successfully";

        const roles: Array<number> = selectedRoles?.map(role => role.id) || [];
        const resorts: Array<number> = selectedResorts?.map(resort => resort.id) || [];

        const response = await Fetch(`user${props.singleData.id ? '/' + props.singleData.id : ''}`, method, { ...body, roles, resorts }, null, props.toast);
        if (response) {
            onHide();
            props.toast.current.show({ severity: 'success', detail: message, life: 3000 });
            props.reloadData();
        }
        setSubmitting(false);
    }

    const onHide = () => {
        props.hideDialog();
        reset({});
        setSelectedRoles(undefined);
    }

    function onSubmit(data: any) {
        if (selectedRoles === undefined || selectedRoles.length < 1) {
            props.toast.current.show({ severity: 'error', detail: "Select at least one Role", life: 3000 });
            return;
        }
        if (selectedResorts === undefined || selectedResorts.length < 1) {
            props.toast.current.show({ severity: 'error', detail: "Select at least one Resort", life: 3000 });
            return;
        }
        saveToDB(data);
    }

    return (
        <Dialog visible={props.open} style={{ width: '80vw' }} header="Add/Edit User" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">
                <JVInput required label="Name" control={control} name="name" error={errors.name?.message} />
                <JVInput required label="Email" control={control} name="email" error={errors.email?.message} />
                <JVInput disabled={props.singleData.employeeCode != null} label="Employee Code" control={control} name="employeeCode" />
                <JVInputMask required label="Contact" mask="9999999999" control={control} name="phoneNumber" />
                <JVInput label="Ivr UserName" control={control} name="kommonuUsername" />
                <JVInput label="Ivr Password" control={control} name="kommonuPassword" />
                <JVInput required={!props.singleData.id} label="Password" control={control} name="password" error={errors.password?.message} />
                <JVMultiselect label="Assign Roles" options={roles} optionLabel="name" placeholder="Select Roles" required value={selectedRoles} onChange={setSelectedRoles} />
                <JVMultiselect label="Assign Resorts" options={resorts} optionLabel="name" placeholder="Select Resorts" required value={selectedResorts} onChange={setSelectedResorts} />
                <JVDropdown required label="Department" control={control} options={departments} optionLabel="name" optionValue="id" name="departmentId" placeholder="Select Department" error={errors.departmentId?.message} />
                <JVDropdown required label="Resort" control={control} options={resorts} optionLabel="name" optionValue="id" name="resortId" placeholder="Select Resort" error={errors.resortId?.message} />
                {/* <JVDropdown label="Manager" control={control} options={manager} optionLabel="name" optionValue="id" name="manager_Id" placeholder="Select Manager" /> */}
                <JVDropdown
                    label="Team Lead"
                    control={control}
                    options={teamLead}
                    optionLabel="name"
                    optionValue="id"
                    name="teamLead_Id"
                    placeholder="Select Team Lead"
                    disabled={isTeamLead}
                />
                {isTeamLead && (
                    <small className="text-500 col-12">
                        This user will automatically become their own Team Lead.
                    </small>
                )}
                <JVCheckbox required label="Active Status" control={control} name="isActive" />
                <div className="col-12 flex flex-wrap gap-4 mt-2">
                    {/* <JVCheckbox label="Is Manager" control={control} name="isManger" column={3} /> */}
                    <JVCheckbox label="Is Team Lead" control={control} name="isTeamLead" column={3} />
                </div>
                <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                    <Button style={{ width: '100px' }} loading={submitting} label="SAVE" type="submit" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </form>
        </Dialog>
    );
}